import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText } from '../../components/common';
import { FiFilter, FiBell, FiTrendingUp, FiDollarSign, FiAlertCircle } from 'react-icons/fi';

interface NotificationItem {
  id: number;
  type: 'activity' | 'information';
  category: 'booking' | 'market' | 'transaction' | 'alert';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

// Mock notification data
const mockNotifications: NotificationItem[] = [
  {
    id: 1,
    type: 'activity',
    category: 'booking',
    title: 'Booking Confirmed',
    message: 'Your session with Dr. Priya Sharma has been confirmed for tomorrow at 10:00 AM',
    time: '2 min ago',
    read: false,
  },
  {
    id: 2,
    type: 'activity',
    category: 'market',
    title: 'Price Alert',
    message: 'Bitcoin (BTC) has crossed ₹50,00,000 - up 5.2% today',
    time: '15 min ago',
    read: false,
  },
  {
    id: 3,
    type: 'activity',
    category: 'transaction',
    title: 'Payment Successful',
    message: 'Your payment of ₹600 for expert consultation has been processed',
    time: '1 hour ago',
    read: true,
  },
  {
    id: 4,
    type: 'information',
    category: 'alert',
    title: 'Market Update',
    message: 'Nifty 50 closes at all-time high. Check the latest market analysis.',
    time: '3 hours ago',
    read: true,
  },
  {
    id: 5,
    type: 'activity',
    category: 'booking',
    title: 'Session Reminder',
    message: 'Your consultation with Rahul Verma starts in 30 minutes',
    time: '5 hours ago',
    read: true,
  },
  {
    id: 6,
    type: 'information',
    category: 'market',
    title: 'Weekly Report',
    message: 'Your portfolio gained 2.5% this week. View detailed report.',
    time: '1 day ago',
    read: true,
  },
  {
    id: 7,
    type: 'activity',
    category: 'transaction',
    title: 'Wallet Credited',
    message: '₹5,000 has been added to your wallet',
    time: '2 days ago',
    read: true,
  },
];

const Notification: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  const [selectedTab, setSelectedTab] = useState<'activity' | 'information'>('activity');
  const [showSortModal, setShowSortModal] = useState(false);
  const [notifications, setNotifications] = useState(mockNotifications);

  const filteredNotifications = notifications.filter((n) => n.type === selectedTab);

  const getIcon = (category: string) => {
    switch (category) {
      case 'booking':
        return FiBell;
      case 'market':
        return FiTrendingUp;
      case 'transaction':
        return FiDollarSign;
      case 'alert':
        return FiAlertCircle;
      default:
        return FiBell;
    }
  };

  const getIconColor = (category: string) => {
    switch (category) {
      case 'booking':
        return colors.primary;
      case 'market':
        return colors.green;
      case 'transaction':
        return '#FFD700';
      case 'alert':
        return colors.alertColor;
      default:
        return colors.primary;
    }
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    // Mark as read
    setNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
    );

    // Navigate based on category
    switch (notification.category) {
      case 'booking':
        navigate('/bookings');
        break;
      case 'market':
        navigate('/market');
        break;
      case 'transaction':
        navigate('/wallet');
        break;
      default:
        // Show notification details
        break;
    }
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const unreadCount = notifications.filter((n) => !n.read && n.type === selectedTab).length;

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(-1)}>
            <CText type="B16" color={colors.textColor}>←</CText>
          </button>
          <CText type="B18" color={colors.textColor}>
            Notifications
          </CText>
          <button onClick={() => setShowSortModal(!showSortModal)}>
            {React.createElement(FiFilter as any, { size: 20, color: colors.textColor })}
          </button>
        </div>

        {/* Tab Selector */}
        <div
          className="flex rounded-lg p-1 mb-4"
          style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor, borderWidth: 1 }}
        >
          {(['information', 'activity'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className="flex-1 py-2 rounded-md transition-colors"
              style={{
                backgroundColor: selectedTab === tab ? colors.inputBackground : 'transparent',
              }}
            >
              <CText
                type="M12"
                color={selectedTab === tab ? colors.textColor : colors.grayScale500}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </CText>
            </button>
          ))}
        </div>

        {/* Unread indicator & Mark all read */}
        {unreadCount > 0 && (
          <div className="flex items-center justify-between mb-4">
            <CText type="r12" color={colors.grayScale500}>
              {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
            </CText>
            <button onClick={markAllAsRead}>
              <CText type="M12" color={colors.primary}>
                Mark all as read
              </CText>
            </button>
          </div>
        )}
      </div>

      {/* Notifications List */}
      <div className="px-4">
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-12">
            <CText type="r14" color={colors.grayScale500}>
              No {selectedTab} notifications
            </CText>
          </div>
        ) : (
          filteredNotifications.map((notification) => {
            const IconComponent = getIcon(notification.category);
            const iconColor = getIconColor(notification.category);

            return (
              <button
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className="w-full flex items-start gap-3 py-4 border-b text-left"
                style={{ borderColor: colors.borderColor }}
              >
                {/* Icon */}
                <div
                  className="p-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: iconColor + '20' }}
                >
                  {React.createElement(IconComponent as any, { size: 20, color: iconColor })}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <CText
                      type={notification.read ? 'r14' : 'M14'}
                      color={colors.textColor}
                    >
                      {notification.title}
                    </CText>
                    {!notification.read && (
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: colors.primary }}
                      />
                    )}
                  </div>
                  <CText
                    type="r12"
                    color={colors.grayScale500}
                    style={{ marginTop: 4 }}
                  >
                    {notification.message}
                  </CText>
                </div>

                {/* Time */}
                <CText type="r12" color={colors.grayScale500} className="flex-shrink-0">
                  {notification.time}
                </CText>
              </button>
            );
          })
        )}
      </div>

      {/* Sort Modal */}
      {showSortModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50"
          onClick={() => setShowSortModal(false)}
        >
          <div
            className="w-full max-w-md rounded-t-2xl p-4"
            style={{ backgroundColor: colors.backgroundColor }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            <CText type="B16" color={colors.textColor} style={{ marginBottom: 16 }}>
              Sort Notifications
            </CText>

            {['Newest first', 'Oldest first', 'Unread first'].map((option) => (
              <button
                key={option}
                className="w-full py-3 text-left border-b"
                style={{ borderColor: colors.borderColor }}
                onClick={() => setShowSortModal(false)}
              >
                <CText type="r14" color={colors.textColor}>
                  {option}
                </CText>
              </button>
            ))}

            <button
              className="w-full py-4 mt-4 rounded-xl"
              style={{ backgroundColor: colors.primary }}
              onClick={() => setShowSortModal(false)}
            >
              <CText type="M14" color={colors.white}>
                Done
              </CText>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notification;
